import { hoursToMilliseconds, minutesToMilliseconds } from 'date-fns';
import type { StoredTeamRole } from '../../../hooks/types';
import type { ShiftPeriod } from '../../Roster/types';
import type { VariableTeamSettings } from '../types';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
dayjs.extend(utc);

export const onTeamRoleSearch = (
  teamRolesToSelect: readonly StoredTeamRole[],
  setTeamRoleOptions: (matchingRoles: readonly StoredTeamRole[]) => void
) => {
  return (value: string) => {
    const matchingRoles: readonly StoredTeamRole[] | undefined =
      teamRolesToSelect.filter((teamRole) =>
        teamRole.name.toLowerCase().includes(value.toLowerCase())
      );

    setTeamRoleOptions(matchingRoles);
  };
};

/**
 * Converts the unknown team_settings return type into a VariableTeamSettings object, or returns undefined.
 * We need to manually convert the unknown jsonb settings object into the types we are expecting
 * since Zeus does not provide these types for us.
 */
export const teamSettingsFromGraphQLUnknown = (
  teamSettings: unknown
): VariableTeamSettings | undefined => {
  if (
    teamSettings !== null &&
    typeof teamSettings === 'object' &&
    'dayShiftPeriod' in teamSettings
  ) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return teamSettings as VariableTeamSettings;
  }
  return undefined;
};

/**
 * The default day shift period.
 * This is given to a new team, and is provided if a current team's settings is null.
 */
export const defaultDayShiftPeriod = {
  start: hoursToMilliseconds(6),
  end: hoursToMilliseconds(18),
};

export const dayShiftHoursConstraint = { min: 8, max: 16 };

/**
 * From an unknown team settings, specifically return the day shift period.
 * Returns the default period if team_settings does not contain `dayShiftPeriod`
 * Returns the period in array form
 **/
export const dayShiftArrayFromUnknownSettings = (
  team_settings: unknown
): readonly number[] =>
  teamSettingsFromGraphQLUnknown(team_settings)?.dayShiftPeriod ?? [
    defaultDayShiftPeriod.start,
    defaultDayShiftPeriod.end,
  ];

export const dayShiftFromUnknownSettings = (
  team_settings: unknown
): ShiftPeriod => {
  const period =
    teamSettingsFromGraphQLUnknown(team_settings)?.dayShiftPeriod ?? [];
  return period[0] !== undefined && period[1] !== undefined
    ? { start: period[0], end: period[1] }
    : defaultDayShiftPeriod;
};

/**
 * Generate the id for items inside antd form.
 * @param names includes form name, form list name, list index and item name.
 * @returns an id that combine all the names with underscore.
 */
export const generateAntdFormItemId = (
  names: readonly (string | number | undefined)[]
) => names.filter((name) => name !== undefined).join('_');

/**
 * Milliseconds from start of the day for the provided hours and minutes.
 * @param hours used to convert to milliseconds.
 * @param minutes used to convert to milliseconds.
 * @returns milliseconds from start of the day.
 */
const millisecondsFromStartOfDay = (hours: number, minutes: number) =>
  hoursToMilliseconds(hours) + minutesToMilliseconds(minutes);

/**
 * Gets the Team's shift period (day/night shift) time.
 * It returns a Day.js instance with a date starts from initial Unix epoch (1970-01-01) with
 * the time in milliseconds from the start the day.
 * E.g. time is 2022-02-10T06:00:00+10:00, then this returns 1970-01-01T06:00:00+00:00.
 * So when the time is stored, it is stored absolute milliseconds from start of Unix epoch regardless of timezone.
 * For above example, it is 21600000.
 * This is done so that we don't need to consider timezone for day/night shift period, it is always hours to hours
 * usage (e.g. 6:00 to 18:00) and we can use the time instance to render a time selector at the same time.
 * @param periodTime a period time that is chosen, could be day shift start time, or end time.
 * @returns a shift period time.
 */
export const getShiftPeriodTime = (
  periodTime: dayjs.Dayjs | null
): dayjs.Dayjs | undefined =>
  periodTime === null
    ? undefined
    : dayjs(
        millisecondsFromStartOfDay(periodTime.hour(), periodTime.minute())
      ).utc();
